import { useMemo, useState } from "react";

import { AlertTriangle, CheckCircle2, Lock, LockOpen } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useCan } from "@/lib/auth";

import {
  useForecastGrid,
  useLockWeek,
  useSnapshots,
  useUnlockWeek,
  type ForecastGridWeek,
} from "../api";
import { DEFAULT_CASH_THRESHOLD, fmtUsd, weekLabel } from "../lib";

/** Row order matches the workbook's Forecast tab. Buckets absent from the data
 *  are simply not shown, so the grid stays as wide as the model actually is. */
const INFLOWS = [
  "Customer Collections",
  "Bank Interest & Refunds",
  "Sublease Income",
  "Other Inflows",
];
const OUTFLOWS = [
  "Payroll & Benefits",
  "Credit Card Settlements",
  "Cost of Service - Comms",
  "Cost of Service - Data & Print",
  "Contractors - Engineering",
  "Contractors - Sales/Marketing",
  "Other Operating Expenses",
  "Professional Services",
  "Rent & Occupancy",
  "Other / Unmapped",
  "Payment Processing Fees",
];

function Amount({ value, bold }: { value: number | undefined; bold?: boolean }) {
  if (value === undefined || value === 0) {
    return <span className="text-muted-foreground/40">—</span>;
  }
  return (
    <span className={`${value < 0 ? "text-danger" : ""} ${bold ? "font-semibold" : ""}`}>
      {fmtUsd(value)}
    </span>
  );
}

/** The lock control, mirroring the CRO forecast's week lock.
 *
 *  Refuses on the server when the week hasn't closed or the bank doesn't tie
 *  to the model — so the button surfaces that reason rather than pretending. */
function LockBar({
  weeks,
  actualsThrough,
}: {
  weeks: ForecastGridWeek[];
  actualsThrough: string | null;
}) {
  const { data: snapshots } = useSnapshots();
  const lock = useLockWeek();
  const unlock = useUnlockWeek();
  const canWrite = useCan("cash_flow.write");
  const canAdmin = useCan("cash_flow.admin");

  const snapshot = useMemo(
    () => (snapshots ?? []).find((s) => s.week_ending === actualsThrough),
    [snapshots, actualsThrough],
  );
  const locked = snapshot?.is_locked ?? false;

  if (!actualsThrough) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              Week ending {weekLabel(actualsThrough, true)}
              {locked ? (
                <Badge variant="success">
                  <Lock className="mr-1 h-3 w-3" />
                  Locked
                </Badge>
              ) : (
                <Badge variant="warning">Open</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Locking freezes this week and records what the model predicts for +1, +6 and
              +13 weeks, so forecast accuracy can be measured later. It refuses if the bank
              statement total doesn't tie to the model.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {locked && canAdmin && (
              <Button
                variant="outline"
                size="sm"
                disabled={unlock.isPending}
                onClick={() => unlock.mutate(actualsThrough)}
              >
                <LockOpen className="h-4 w-4" />
                Unlock
              </Button>
            )}
            {!locked && (
              <Button
                disabled={!canWrite || lock.isPending}
                onClick={() => lock.mutate({ weekEnding: actualsThrough })}
              >
                <Lock className="h-4 w-4" />
                {lock.isPending ? "Locking…" : "Lock week"}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-sm">
        {snapshot?.tie_out_variance !== undefined && snapshot?.tie_out_variance !== null && (
          <p
            className={`inline-flex items-center gap-1.5 ${
              Math.abs(snapshot.tie_out_variance) < 0.01 ? "text-success" : "text-danger"
            }`}
          >
            {Math.abs(snapshot.tie_out_variance) < 0.01 ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            Tie-out{" "}
            {Math.abs(snapshot.tie_out_variance) < 0.01
              ? "ties to the bank"
              : `off by ${fmtUsd(snapshot.tie_out_variance)}`}
            {snapshot.locked_by_name && ` · locked by ${snapshot.locked_by_name}`}
          </p>
        )}
        {lock.isError && (
          <p className="rounded-md border border-danger/40 bg-danger-light p-3 text-danger">
            {lock.error.message}
          </p>
        )}
        {lock.isSuccess && (
          <p className="text-muted-foreground">
            Locked · <b className="text-foreground">{lock.data.captured_rows}</b> forecast rows
            captured, {lock.data.actualized_rows} earlier predictions actualized.
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          {weeks.filter((w) => w.segment === "actual").length} actual weeks ·{" "}
          {weeks.filter((w) => w.segment === "forecast").length} forecast weeks in view.
        </p>
      </CardContent>
    </Card>
  );
}

/** The workbook's Forecast tab: buckets down, weeks across.
 *
 *  Replaces the four-total table this tab used to show. Actual columns are
 *  bank-tied; forecast columns are summed from the drivers. */
export function WeeklyDetailTab({ cashThreshold }: { cashThreshold?: number }) {
  const [horizon, setHorizon] = useState(13);
  const { data, isLoading } = useForecastGrid({ horizonWeeks: horizon, historyWeeks: 8 });
  const threshold = cashThreshold ?? DEFAULT_CASH_THRESHOLD;
  const completeThrough = data?.complete_through ?? null;

  const weeks = useMemo(() => data?.weeks ?? [], [data]);
  const present = useMemo(() => {
    const seen = new Set<string>();
    weeks.forEach((w) => Object.keys(w.buckets).forEach((b) => seen.add(b)));
    return {
      inflows: INFLOWS.filter((b) => seen.has(b)),
      outflows: OUTFLOWS.filter((b) => seen.has(b)),
    };
  }, [weeks]);

  if (isLoading) {
    return (
      <div className="rounded-lg border p-10 text-center text-sm text-muted-foreground">
        Computing forecast…
      </div>
    );
  }

  const row = (label: string, get: (w: ForecastGridWeek) => number | undefined, opts?: {
    bold?: boolean;
    indent?: boolean;
    divider?: boolean;
  }) => (
    <tr key={label} className={opts?.divider ? "border-t" : undefined}>
      <th
        scope="row"
        className={`sticky left-0 z-10 bg-card py-1.5 pr-4 text-left text-sm font-normal ${
          opts?.bold ? "font-semibold" : ""
        } ${opts?.indent ? "pl-4" : ""}`}
      >
        {label}
      </th>
      {weeks.map((w) => (
        <td key={w.week_ending} className="whitespace-nowrap px-3 py-1.5 text-right text-sm">
          <Amount value={get(w)} bold={opts?.bold} />
        </td>
      ))}
    </tr>
  );

  return (
    <div className="flex flex-col gap-6">
      <LockBar weeks={weeks} actualsThrough={data?.actuals_through ?? null} />

      {data && data.warnings.length > 0 && (
        <div className="rounded-lg border border-warning/40 bg-warning-light p-4 text-sm">
          <p className="mb-1 font-medium">
            Assumptions need extending — later weeks understate cash
          </p>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            {data.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">
            A driver with nothing scheduled contributes zero, not a repeat of last month — so
            an out-of-date assumption reads as good news. See{" "}
            <span className="font-medium">How to use</span> for what maintains itself and what
            doesn't.
          </p>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Weekly detail</CardTitle>
              <CardDescription>
                Bank-tied actuals through {weekLabel(data?.actuals_through ?? "", true)}, then
                the driver forecast. Collections run rate{" "}
                <b className="text-foreground">{fmtUsd(data?.collections_run_rate ?? 0)}</b>/mo.
                Ending cash below {fmtUsd(threshold)} shows red.
              </CardDescription>
            </div>
            <div className="flex gap-1">
              {[13, 26, 52].map((h) => (
                <Button
                  key={h}
                  variant={horizon === h ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHorizon(h)}
                >
                  {h}w
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-0">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-card py-2 pr-4 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Week ending
                  </th>
                  {weeks.map((w) => {
                    const underDriven =
                      Boolean(completeThrough) && w.week_ending > completeThrough!;
                    return (
                      <th
                        key={w.week_ending}
                        className={`whitespace-nowrap px-3 py-2 text-right text-xs font-medium ${
                          underDriven ? "bg-warning-light/40" : ""
                        }`}
                        title={
                          underDriven
                            ? "Not fully driven — some assumptions have no scheduled amount this far out, so this week understates cash movement."
                            : undefined
                        }
                      >
                        <span className="block">{weekLabel(w.week_ending)}</span>
                        <Badge
                          variant={
                            w.segment === "actual"
                              ? "success"
                              : underDriven
                                ? "warning"
                                : "info"
                          }
                          className="mt-1"
                        >
                          {w.segment === "actual" ? "Act" : underDriven ? "Partial" : "Fcst"}
                        </Badge>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {row("Beginning cash", (w) => w.beginning_cash, { divider: true })}
                {present.inflows.map((b) =>
                  row(b, (w) => w.buckets[b], { indent: true }),
                )}
                {row("Total cash inflows", (w) => w.total_inflows, {
                  bold: true,
                  divider: true,
                })}
                {present.outflows.map((b) =>
                  row(b, (w) => w.buckets[b], { indent: true }),
                )}
                {row("Total cash outflows", (w) => w.total_outflows, {
                  bold: true,
                  divider: true,
                })}
                {row("Net cash change", (w) => w.net_change, { bold: true })}
                <tr className="border-t">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-card py-1.5 pr-4 text-left text-sm font-semibold"
                  >
                    Ending cash balance
                  </th>
                  {weeks.map((w) => (
                    <td
                      key={w.week_ending}
                      className="whitespace-nowrap px-3 py-1.5 text-right text-sm"
                    >
                      <span
                        className={
                          w.ending_cash < 0
                            ? "font-semibold text-danger"
                            : w.ending_cash < threshold
                              ? "font-semibold text-danger"
                              : "font-semibold"
                        }
                      >
                        {fmtUsd(w.ending_cash)}
                      </span>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
